import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { API_BASE_URL } from '../lib/config';


interface WebSocketContextType {
    socket: Socket | null;
    isConnected: boolean;
    subscribe: (event: string, callback: (data: any) => void) => () => void;
    emit: (event: string, data: any) => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export const useWebSocket = () => {
    const context = useContext(WebSocketContext);
    if (!context) {
        throw new Error('useWebSocket must be used within WebSocketProvider');
    }
    return context;
};

interface WebSocketProviderProps {
    children: React.ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
    const [socket, setSocket] = useState<Socket | null>(null);
    const [isConnected, setIsConnected] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('auth_token');

        if (!token) {
            console.log('No auth token found, skipping WebSocket connection');
            return;
        }

        // Connect to WebSocket server
        const socketInstance = io(API_BASE_URL, {
            path: '/socket.io/',
            query: { token },
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            reconnectionAttempts: 5
        });

        socketInstance.on('connect', () => {
            console.log('WebSocket connected');
            setIsConnected(true);
        });

        socketInstance.on('disconnect', () => {
            console.log('WebSocket disconnected');
            setIsConnected(false);
        });

        socketInstance.on('connect_error', (error) => {
            console.error('WebSocket connection error:', error);
            setIsConnected(false);
        });

        setSocket(socketInstance);

        // Cleanup on unmount
        return () => {
            console.log('Cleaning up WebSocket connection');
            socketInstance.disconnect();
        };
    }, []);

    const subscribe = useCallback((event: string, callback: (data: any) => void) => {
        if (!socket) {
            console.warn(`Cannot subscribe to ${event}: socket not initialized`);
            return () => { };
        }

        socket.on(event, callback);

        // Return unsubscribe function
        return () => {
            socket.off(event, callback);
        };
    }, [socket]);

    const emit = useCallback((event: string, data: any) => {
        if (!socket) {
            console.warn(`Cannot emit ${event}: socket not initialized`);
            return;
        }

        socket.emit(event, data);
    }, [socket]);

    const value: WebSocketContextType = {
        socket,
        isConnected,
        subscribe,
        emit
    };

    return (
        <WebSocketContext.Provider value={value}>
            {children}
        </WebSocketContext.Provider>
    );
};
